/**
 * One delete affordance for the whole app (task 864).
 *
 * The trigger, the flag that hides it, the confirmation, the mutation call, and
 * the toast that reports the outcome — all in one component, because the four of
 * them are one decision and splitting them is exactly how the app ended up with
 * four confirmation patterns and two destructive actions with no confirmation at
 * all.
 *
 * The caller supplies only what is genuinely its own: which flag governs the
 * affordance, what the thing is called, and the mutation to run. `onDelete` must
 * REJECT on failure (a `mutateAsync`, not a `mutate`), because the rejection is
 * what carries the service's refusal — and the rule since task 858 is that the
 * User is told what the service said, never a guess.
 *
 * `restorable` is not decoration. Deleting an agent writes a ledger row and the
 * Archive can put it back (task 862); removing a custom domain is an AMS
 * operation with no ledger behind it, and revoking an invitation is not a
 * resource delete at all. Promising a restore that does not exist would be the
 * worst kind of wrong, so those callers pass `restorable={false}` and the
 * dialog and the toast both go quiet about the Archive.
 */
import { useState, type ReactNode } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { DeleteResourceDialog } from './DeleteResourceDialog';
import { useFlag } from '../../hooks/useFeatureFlags';
import { useOptionalToast } from '../../contexts/ToastContext';
import { refusalReason } from '../../utils/apiRefusal';
import type { FlagName } from '../../types/feature-flags.generated';
import type { DependentsEntity } from '../../types/dependents';

export interface DeleteResourceControlProps {
  /** The affordance flag — the twin of the backend gate on this entity's DELETE. */
  flag: FlagName;
  /** The dependents route, for the entities that have one. */
  entity?: DependentsEntity;
  /** Lower case, used mid-sentence: "agent", "knowledge base", "custom domain". */
  entityLabel: string;
  /** The resource's own name, quoted in the dialog title. */
  name: string;
  id: string;
  /** Extra sentences: what leaves and what stays, in this entity's terms. */
  body?: ReactNode;
  /** False when the delete writes no ledger row and so cannot be restored. */
  restorable?: boolean;
  /** When set, the User must type this before the confirm enables. */
  confirmText?: string;
  /** The mutation. Must reject on failure so the refusal can be reported. */
  onDelete: () => Promise<unknown>;
  /** Runs after a successful delete — navigate away, clear a selection. */
  onDeleted?: () => void;
  /** `icon` for a row or card; `button` for a detail-page header action. */
  variant?: 'icon' | 'button';
  /** Overrides the trigger's label. Defaults to "Delete". */
  triggerLabel?: string;
  testId?: string;
  /** True inside a row or card that navigates on click. */
  stopPropagation?: boolean;
}

export function DeleteResourceControl({
  flag,
  entity,
  entityLabel,
  name,
  id,
  body,
  restorable = true,
  confirmText,
  onDelete,
  onDeleted,
  variant = 'icon',
  triggerLabel = 'Delete',
  testId,
  stopPropagation = false,
}: DeleteResourceControlProps) {
  const enabled = useFlag(flag);
  /**
   * The Archive can only bring a resource back while Restore is offered there
   * (task 1240): with `innovate.fe.archive.restore` off, neither the dialog nor
   * the toast promises a restore. The toast still links the Archive, which
   * keeps listing what was deleted.
   */
  const restoreEnabled = useFlag('innovate.fe.archive.restore');
  const promiseRestore = restorable && restoreEnabled;
  const toast = useOptionalToast();
  const [isOpen, setIsOpen] = useState(false);
  // Local rather than the mutation's own `isPending`: several callers share one
  // mutation across a whole list, and one row's delete must not put every other
  // row's dialog into a spinner.
  const [isDeleting, setIsDeleting] = useState(false);

  if (!enabled) return null;

  const confirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      setIsOpen(false);
      toast?.success(
        `Deleted ${name}`,
        promiseRestore ? 'You can restore it from Settings → Archive.' : undefined,
        // The link is what makes the promise actionable (task 865); a delete
        // with no ledger row behind it gets neither the sentence nor the link.
        restorable ? { label: 'View Archive', href: '/settings/archive' } : undefined
      );
      onDeleted?.();
    } catch (error) {
      toast?.error(`Could not delete this ${entityLabel}`, refusalReason(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const open = (event: React.MouseEvent) => {
    if (stopPropagation) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsOpen(true);
  };

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={open}
          data-testid={testId}
          aria-label={`Delete ${name}`}
          title={`Delete ${entityLabel}`}
          className="inline-flex items-center p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={open}
          data-testid={testId}
          className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-500 hover:border-red-300 hover:text-red-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-red-800 dark:hover:text-red-400"
        >
          <TrashIcon className="-ml-1 mr-2 h-4 w-4" />
          {triggerLabel}
        </button>
      )}

      {isOpen && (
        // The dialog renders through a portal, but React still bubbles its
        // clicks to this control's React ancestors. Inside a row or card that
        // acts on click, Confirm and Cancel would then also trigger the row —
        // in the artifacts list, opening the deleted artifact's version history
        // and reading every version (task 1161). The wrapper stops them here.
        <span
          className="contents"
          onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
        >
          <DeleteResourceDialog
            id={id}
            entity={entity}
            entityLabel={entityLabel}
            name={name}
            body={body}
            restorable={promiseRestore}
            confirmText={confirmText}
            isPending={isDeleting}
            onClose={() => setIsOpen(false)}
            onConfirm={confirm}
          />
        </span>
      )}
    </>
  );
}

export default DeleteResourceControl;
